import {ChangeDetectionStrategy, Component, HostListener, OnInit} from '@angular/core';
import {IsGranted} from '../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Pool} from '../../../../../models/landing-page/pool';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {LandingPageService} from '../../../../../services/landing-page/landing-page.service';
import {PoolService} from '../../../../../services/landing-page/pool.service';
import {forkJoin, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {PageTypeService} from '../../../../../services/page-type/page-type.service';
import {PageType} from '../../../../../models/page-type';
import {LandingPage} from '../../../../../models/landing-page/landing-page';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import {ConfirmDirective} from '../../../../../directives/confirm/confirm.directive';
import {ElementLoaderComponent} from '../../../../layout/element-loader/element-loader.component';
import {PoolLandingPage} from '../../../../../models/landing-page/pool-landing-page';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {OfferService} from '../../../../../services/offer/offer.service';
import {Offer} from '../../../../../models/offer';
import {DialogService} from '../../../../../services/dialog/dialog.service';
import {ParameterFormComponent} from './parameter-form/parameter-form.component';
import {PoolMode} from '../../../../../constants/pool-mode';

@Component({
  selector: 'app-landing-page-pool-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    NgForOf,
    NgIf,
    ConfirmDirective,
    ElementLoaderComponent,
    NgClass,
    InputSwitchComponent
  ],
  templateUrl: './landing-page-pool-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './landing-page-pool-form.component.scss'
})
@IsGranted(['LANDING_PAGE_VIEW'])
@MetaTitle('Pool')
export class LandingPagePoolFormComponent implements OnInit {
  loaded = false;
  id: number | null = null;
  pool: Pool | null = null;
  types: PageType[] = [];
  landingPageQuery: string = '';
  landingPageResults: LandingPage[] = [];
  offers: Offer[] = [];
  searchLoading = false;
  searchShow = false;
  ignoreClick = false;

  private searchQuery$ = new Subject<string>();

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    identifier: new FormControl('', Validators.required),
    typeId: new FormControl<number | null>(null, Validators.required),
    enabled: new FormControl(true),
    mode: new FormControl(PoolMode.RANDOM),
    cappingCount: new FormControl<number | null>(null),
    cappingDuration: new FormControl<number | null>(null),
  });

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    protected landingPageService: LandingPageService,
    private poolService: PoolService,
    private pageTypeService: PageTypeService,
    private offerService: OfferService,
    private dialogService: DialogService,
  ) {
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    this.searchQuery$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: string) => query
        ? this.landingPageService.findAll({name: query, sort: 'name'})
        : of([])
      )
    ).subscribe((results: LandingPage[]) => {
      this.landingPageResults = results;
      this.searchLoading = false;
      this.searchShow = results.length > 0;
    });

    this.loader.startLoading();

    forkJoin([
      this.pageTypeService.findAll({sort: 'name'}),
      this.offerService.findAll()
    ]).subscribe(([types, offers]) => {
      this.types = types;
      this.offers = offers;

      if (this.id) {
        this.poolService.findOneById(this.id, {groups: 'poolLandingPages,landingPage'}).subscribe((pool: Pool) => {
          this.pool = pool;

          this.form.patchValue({
            name: this.pool.name,
            identifier: this.pool.identifier,
            typeId: this.pool.type?.id ?? null,
            enabled: this.pool.enabled,
            mode: this.pool.mode,
            cappingCount: this.pool.cappingCount ?? 0,
            cappingDuration: this.pool.cappingDuration ?? 0,
          });

          this.generatePosition();

          this.loader.stopLoading();
          this.loaded = true;
        });
      } else {
        this.pool = new Pool();
        this.loader.stopLoading();
        this.loaded = true;
      }
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    const params: any = this.form.value;

    switch (params.mode) {
      case PoolMode.RANDOM:
        params.cappingCount = null;
        params.cappingDuration = null;
        break;
      case PoolMode.ORDERED_CAPPING:
        params.cappingCount = params.cappingCount > 0 ? params.cappingCount : 0;
        params.cappingDuration = params.cappingDuration > 0 ? params.cappingDuration : 0;
        break;
    }

    params.poolLandingPages = this.pool?.poolLandingPages.map((poolLandingPage: PoolLandingPage) => {
      return {
        id: poolLandingPage.id ?? null,
        landingPageId: poolLandingPage.landingPage?.id,
        offerId: poolLandingPage.offerId,
        weight: poolLandingPage.weight,
        enabled: poolLandingPage.enabled,
        parameters: poolLandingPage.parameters,
        position: poolLandingPage.position,
      }
    });

    if (this.isCreate()) {
      this.poolService.create(this.form.value).subscribe(() => {
        this.loader.stopLoading();
        this.router.navigateByUrl('promotion-tools/landing-pages-pools').then(() => {
          this.snackbar.success('Pool has been created');
        });
      });
    } else if (this.pool?.id) {
      this.poolService.update(this.pool.id, this.form.value).subscribe(() => {
        this.loader.stopLoading();
        this.router.navigateByUrl('promotion-tools/landing-pages-pools').then(() => {
          this.snackbar.success('Pool has been modified.');
        });
      });
    }
  }

  isCreate(): boolean {
    return !this.id;
  }

  add(landingPage: LandingPage): void {
    if (!landingPage || !this.pool) {
      return;
    }

    if (this.pool.poolLandingPages.some(lp => lp.landingPage?.id === landingPage.id)) {
      this.snackbar.warning('This promotion link is already in the pool.', 2500);
      return;
    }

    const poolLandingPage = new PoolLandingPage();
    poolLandingPage.landingPage = landingPage;
    poolLandingPage.weight = 100;
    this.pool.poolLandingPages.push(poolLandingPage);
    this.generatePosition();
  }

  remove(poolLandingPage: PoolLandingPage): void {
    const i: number = this.pool?.poolLandingPages.indexOf(poolLandingPage) ?? -1;
    if (i !== -1) {
      this.pool?.poolLandingPages.splice(i, 1);
    }
  }

  generatePosition(): void {
    let position = 1;
    this.pool?.poolLandingPages.forEach((poolLandingPage: PoolLandingPage) => {
      poolLandingPage.position = position++;
    });
  }

  search(): void {
    this.searchLoading = !!this.landingPageQuery;
    this.searchQuery$.next(this.landingPageQuery);
  }

  focus(): void {
    if (this.landingPageResults.length > 0) {
      this.searchShow = true;
    }
  }

  landingPageSearchClick(): void {
    this.ignoreClick = true;
  }

  @HostListener('body:click')
  onBodyClick(): void {
    if (!this.ignoreClick) {
      this.searchShow = false;
    }
    this.ignoreClick = false;
  }

  getParameters(poolLandingPage: PoolLandingPage): string {
    const searchParams = new URLSearchParams(poolLandingPage.parameters);
    return searchParams.toString();
  }

  openParameterForm(poolLandingPage: PoolLandingPage): void {
    this.dialogService.openDialog(ParameterFormComponent, {
      data: poolLandingPage
    }).subscribe((params: any) => {
      if (params !== null) {
        poolLandingPage.parameters = params;
      }
    });
  }

  isCappingMode(): boolean {
    return this.form.get('mode')?.value === PoolMode.ORDERED_CAPPING;
  }

  up(poolLandingPage: PoolLandingPage): void {
    const i = this.pool?.poolLandingPages.indexOf(poolLandingPage);
    if (i !== undefined && i > 0) {
      this.pool?.poolLandingPages.splice(i, 1);
      this.pool?.poolLandingPages.splice(i - 1, 0, poolLandingPage);
      this.generatePosition();
    }
  }

  down(poolLandingPage: PoolLandingPage): void {
    if (this.pool?.poolLandingPages) {
      const i = this.pool?.poolLandingPages.indexOf(poolLandingPage);
      if (i !== undefined && i < this.pool?.poolLandingPages.length - 1) {
        this.pool?.poolLandingPages.splice(i, 1);
        this.pool?.poolLandingPages.splice(i + 1, 0, poolLandingPage);
        this.generatePosition();
      }
    }
  }

  protected readonly PoolMode = PoolMode;
}
